/**
 * The signed-in teammate's dashboard access: permission codes, locked list
 * filters and own-records-only (per tab), as resolved by the server (`team/my_access/`).
 *
 * Kept in Vuex so every `$can(...)` re-renders the moment access is refetched --
 * after an admin changes someone's permissions they apply on the next refresh
 * (window focus, or any "permission denied" response), not at the next login.
 *
 * Mirrored to localStorage (app-js/authStorage) so a page reload, or a second
 * tab, renders the right tabs before the refetch returns. It belongs to the
 * shared login, so logout (app-js/session.js) clears it in every tab.
 */
import { getAuth, setAuth, removeAuth } from "@/app-js/authStorage";

const STORAGE_KEY = "orrish.access";
const REFRESH_THROTTLE_MS = 60 * 1000;

const empty = () => ({
  loaded: false,
  // Holds every permission: a Super Admin (or platform staff). An Admin is
  // not -- their codes are what a Super Admin gave them.
  isAdmin: false,
  isDealer: false,
  codes: [],
  lockedFilters: {},
  ownRecordsOnly: false,
  ownRecordsTabs: [],
  groups: [],
  // What a Super Admin let them give while managing permissions: codes and
  // role names, or null for no limit (everything they hold / roles below Admin).
  grantCodes: null,
  assignableRoles: null,
  version: "",
  fetchedAt: 0,
});

function fromPayload(payload) {
  return {
    loaded: true,
    isAdmin: !!payload.is_admin,
    isDealer: !!payload.is_dealer,
    codes: Array.isArray(payload.permissions) ? payload.permissions : [],
    lockedFilters: payload.locked_filters || {},
    ownRecordsOnly: !!payload.own_records_only,
    ownRecordsTabs: Array.isArray(payload.own_records_tabs) ? payload.own_records_tabs : [],
    groups: Array.isArray(payload.groups) ? payload.groups : [],
    grantCodes: Array.isArray(payload.grant_codes) ? payload.grant_codes : null,
    assignableRoles: Array.isArray(payload.assignable_roles) ? payload.assignable_roles : null,
    version: payload.version || "",
    fetchedAt: Date.now(),
  };
}

function readStored() {
  try {
    const saved = JSON.parse(getAuth(STORAGE_KEY) || "null");
    return saved && typeof saved === "object" ? saved : null;
  } catch (e) {
    return null;
  }
}

let inflight = null;

export default {
  namespaced: true,
  state: () => {
    const saved = readStored();
    return saved ? Object.assign(empty(), fromPayload(saved)) : empty();
  },
  getters: {
    loaded: (state) => state.loaded,
    isAdmin: (state) => state.isAdmin,
    isDealer: (state) => state.isDealer,
    codeSet: (state) => new Set(state.codes),
    can: (state, getters) => (code) => {
      if (!code) return true;
      if (state.isAdmin) return true;
      if (Array.isArray(code)) return code.some((c) => getters.codeSet.has(c));
      return getters.codeSet.has(code);
    },
    lockedFilters: (state) => state.lockedFilters || {},
    lockedFilter: (state) => (tab, key) =>
      ((state.lockedFilters || {})[tab] || {})[key] || null,
    isFilterLocked: (state, getters) => (tab, key) =>
      !!getters.lockedFilter(tab, key),
    ownRecordsOnly: (state) => state.ownRecordsOnly,
    ownRecordsOn: (state) => (tab) => (state.ownRecordsTabs || []).includes(tab),
    groups: (state) => state.groups || [],
    grantCodes: (state) => state.grantCodes,
    assignableRoles: (state) => state.assignableRoles,
  },
  mutations: {
    SET_ACCESS(state, payload) {
      Object.assign(state, fromPayload(payload || {}));
      try {
        setAuth(STORAGE_KEY, JSON.stringify(payload || {}));
      } catch (e) {
        // A full or blocked storage must not break sign-in.
      }
    },
    CLEAR(state) {
      Object.assign(state, empty());
      try {
        removeAuth(STORAGE_KEY);
      } catch (e) {
        /* ignore */
      }
    },
  },
  actions: {
    setFromPayload({ commit }, payload) {
      if (payload) commit("SET_ACCESS", payload);
    },
    /**
     * Refetch from the server. `force` skips the throttle (used after a
     * permission-denied response). Resolves once state is current; never rejects.
     */
    fetchAccess({ commit, state }, { force = false } = {}) {
      if (!getAuth("token")) return Promise.resolve();
      if (inflight) return inflight;
      if (!force && state.loaded && Date.now() - state.fetchedAt < REFRESH_THROTTLE_MS) {
        return Promise.resolve();
      }
      // Imported lazily: the request helper imports the store and router, so a
      // static import here would be circular at module-evaluation time.
      inflight = Promise.all([import("@/app-js/new-base-axios"), import("@/app-js/api-urls")])
        .then(([{ default: request }, { default: urls }]) => {
          return new Promise((resolve) => {
            request("get", urls.MY_ACCESS, {
              showErrorMessage: false,
              onSuccess: (res) => commit("SET_ACCESS", res.data.result),
              onFinally: resolve,
            });
          });
        })
        .finally(() => {
          inflight = null;
        });
      return inflight;
    },
    ensureLoaded({ state, dispatch }) {
      return state.loaded ? Promise.resolve() : dispatch("fetchAccess", { force: true });
    },
    clear({ commit }) {
      commit("CLEAR");
    },
  },
};
